import React, { useState } from 'react';
import '../styles/volunteer.css';
import {
  getVolunteerFormInitialState,
  submitVolunteerApplication,
} from '../services/volunteerService';
import { getPersonNameError, sanitizePersonNameInput } from '../utils/personName';

const MailIcon = () => (
  <svg className="volunteer-program-mail-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3 7l9 6 9-6" />
  </svg>
);

const volunteerPrograms = [
  {
    id: 'music',
    layout: 'image-left',
    contactTheme: 'blue',
    image: 'volunteer-program-music.png',
    imageAlt: 'Clase de música con estudiantes practicando bajo eléctrico',
    title: 'Profesores de música para niños desfavorecidos',
    text:
      'Si eres un maestro de música con experiencia en Kansas (sin importar en qué ciudad vivas), eres bienvenido para que te unas a nuestro programa Music Link, creando oportunidades para brindar lecciones de música a estudiantes menores de 18 años de edad que no pueden pagar el precio completo de las clases. Nuestro programa apoya a estudiantes de música desde 1993.',
    contacts: [
      {
        label: 'Coordinación',
        names: 'Carolina Lugo, Stefi Carillo',
        emails: ['comunidad@beyondlimitsfoundation.org'],
        avatars: ['CL', 'SC'],
      },
      {
        label: 'Dirección',
        names: 'Carolina Lugo',
        emails: ['info@beyondlimitsfoundation.org'],
        avatars: ['CL'],
      },
    ],
  },
  {
    id: 'hospice',
    layout: 'image-right',
    contactTheme: 'magenta',
    image: 'volunteer-program-hospice.png',
    imageAlt: 'Voluntarios sonriendo con las manos extendidas',
    title: 'Voluntarios de hospicio',
    text:
      'El equipo de Enhabit Hospice se dedica a mejorar la calidad de vida de los pacientes terminales y sus familias, brindando cuidados compasivos que promueven la dignidad, el confort y el apoyo emocional y espiritual en cada etapa del proceso.',
    contacts: [
      {
        label: 'Coordinación',
        names: 'Carolina Lugo',
        emails: ['info@beyondlimitsfoundation.org'],
        avatars: ['CL'],
      },
    ],
  },
  {
    id: 'university',
    layout: 'image-left',
    contactTheme: 'lavender',
    image: 'volunteer-program-university.png',
    imageAlt: 'Estudiantes universitarios en un aula',
    title: 'Voluntariado para estudiantes universitarios',
    text:
      'Este programa está dirigido a estudiantes de medicina que desean interactuar con pacientes a través de visitas, juegos, lectura y acompañamiento emocional, fortaleciendo su formación humana y su compromiso con el servicio a la comunidad.',
    contacts: [
      {
        label: 'Coordinación',
        names: 'Stefi Carillo',
        emails: ['info@beyondlimitsfoundation.org'],
        avatars: ['SC'],
      },
    ],
  },
  {
    id: 'connection',
    layout: 'image-right',
    contactTheme: 'cream',
    image: 'volunteer-program-connection.png',
    imageAlt: 'Voluntarios plantando un árbol en equipo',
    title: 'Voluntario de Conexión Humana',
    text:
      'Programa de conversaciones individuales de 45 a 60 minutos entre voluntarios de distintas partes del mundo, enfocadas en la curiosidad mutua y la escucha activa, sin enseñar ni dar consejos, sino creando un espacio de conexión auténtica.',
    contacts: [
      {
        label: 'Coordinación',
        names: 'Maria Luisa Carrasco',
        emails: ['info@beyondlimitsfoundation.org'],
        avatars: ['MC'],
      },
    ],
  },
];

const VolunteerPage = () => {
  const [formData, setFormData] = useState(getVolunteerFormInitialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState('');

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    const nextValue = name === 'nombreCompleto' ? sanitizePersonNameInput(value) : value;
    setFormData((current) => ({ ...current, [name]: nextValue }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setFormMessage('');

    const nameError = getPersonNameError(formData.nombreCompleto);
    if (nameError) {
      setFormMessage(nameError);
      return;
    }

    setIsSubmitting(true);

    submitVolunteerApplication(formData)
      .then(() => {
        setFormData(getVolunteerFormInitialState());
        setFormMessage('Tu solicitud fue enviada correctamente. Nos pondremos en contacto contigo pronto.');
      })
      .catch((error) => {
        setFormMessage(error.message || 'No se pudo enviar la solicitud.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="volunteer-root">
      <section className="volunteer-section volunteer-section-hero">
        <div className="volunteer-hero-card">
          <div className="volunteer-hero-copy">
            <h1 className="volunteer-hero-title">Voluntariado</h1>
            <p className="volunteer-hero-text">
              <strong>Beyond Limits Foundation</strong> impulsa el voluntariado como un puente vital,
              reconociendo que cada etapa de la vida tiene talentos únicos y energía para contribuir con la
              sociedad; transformando así la idea tradicional de ayuda en una de intercambio activo, propósito y
              crecimiento mutuo.
            </p>
            <p className="volunteer-hero-text">
              <strong>Formamos una comunidad de casi 100 personas,</strong> conectadas a través de nuestro
              informativo mensual y nuestras redes de comunicación (correo, WhatsApp y redes sociales),
              invitándolas a participar en diversas actividades de voluntariado presencial y virtual, donde su
              experiencia y pasión encuentran una causa significativa para impactar.
            </p>
          </div>

          <div className="volunteer-hero-visual">
            <img
              src={`${process.env.PUBLIC_URL}/images/volunteer-hero-photo.png`}
              alt="Voluntarios sonriendo con las manos extendidas"
              className="volunteer-hero-photo"
            />
          </div>
        </div>
      </section>

      <section className="volunteer-section volunteer-section-programs">
        <div className="volunteer-programs-inner">
          {volunteerPrograms.map((program) => (
            <article
              key={program.id}
              className={`volunteer-program-card volunteer-program-card--${program.layout}`}
            >
              <div className="volunteer-program-visual">
                <img
                  src={`${process.env.PUBLIC_URL}/images/${program.image}`}
                  alt={program.imageAlt}
                  className="volunteer-program-photo"
                />
              </div>

              <div className="volunteer-program-copy">
                <h2 className="volunteer-program-title">{program.title}</h2>
                <p className="volunteer-program-text">{program.text}</p>
              </div>

              <aside className={`volunteer-program-contact volunteer-program-contact--${program.contactTheme}`}>
                {program.contacts.map((contact) => (
                  <div key={`${program.id}-${contact.label}`} className="volunteer-program-contact-block">
                    <div className="volunteer-program-avatars" aria-hidden="true">
                      {contact.avatars.map((initials) => (
                        <span key={initials} className="volunteer-program-avatar">
                          {initials}
                        </span>
                      ))}
                    </div>
                    <p className="volunteer-program-contact-label">{contact.label}</p>
                    <p className="volunteer-program-contact-names">{contact.names}</p>
                    {contact.emails.map((email) => (
                      <a key={email} href={`mailto:${email}`} className="volunteer-program-contact-email">
                        <MailIcon />
                        <span>{email}</span>
                      </a>
                    ))}
                  </div>
                ))}
              </aside>
            </article>
          ))}
        </div>
      </section>

      <section className="volunteer-section volunteer-section-apply">
        <div className="volunteer-apply-card">
          <div className="volunteer-apply-inner">
            <h2 className="volunteer-apply-title">¿Quieres Ser Voluntario?</h2>
            <p className="volunteer-apply-intro">
              ¡Te invitamos a ser parte de este movimiento! Buscamos personas que deseen darle vida a sus ideas,
              aptitudes y conocimientos. Que tengan curiosidad por aprender, emprender, crear, innovar,
              reinventarse y, mucho más.
            </p>

            <form className="volunteer-apply-form" onSubmit={handleSubmit}>
              <div className="volunteer-apply-field volunteer-apply-field--full">
                <label className="volunteer-apply-label" htmlFor="nombreCompleto">
                  Nombre completo
                </label>
                <input
                  id="nombreCompleto"
                  name="nombreCompleto"
                  type="text"
                  className="volunteer-apply-input"
                  placeholder="Ingresa tu nombre completo"
                  value={formData.nombreCompleto}
                  onChange={handleFieldChange}
                  required
                />
              </div>

              <div className="volunteer-apply-row">
                <div className="volunteer-apply-field">
                  <label className="volunteer-apply-label" htmlFor="profesion">
                    Profesión u oficio
                  </label>
                  <input
                    id="profesion"
                    name="profesion"
                    type="text"
                    className="volunteer-apply-input"
                    placeholder="Ingresa tu profesión u oficio"
                    value={formData.profesion}
                    onChange={handleFieldChange}
                    required
                  />
                </div>

                <div className="volunteer-apply-field">
                  <label className="volunteer-apply-label" htmlFor="edad">
                    Edad
                  </label>
                  <input
                    id="edad"
                    name="edad"
                    type="text"
                    className="volunteer-apply-input"
                    placeholder="Ingresa tu edad"
                    value={formData.edad}
                    onChange={handleFieldChange}
                    required
                  />
                </div>
              </div>

              <div className="volunteer-apply-row">
                <div className="volunteer-apply-field">
                  <label className="volunteer-apply-label" htmlFor="email">
                    Correo electrónico
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className="volunteer-apply-input"
                    placeholder="Ingresa tu correo"
                    value={formData.email}
                    onChange={handleFieldChange}
                    required
                  />
                </div>

                <div className="volunteer-apply-field">
                  <label className="volunteer-apply-label" htmlFor="telefono">
                    Teléfono
                  </label>
                  <input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    className="volunteer-apply-input"
                    placeholder="Ingresa tu teléfono"
                    value={formData.telefono}
                    onChange={handleFieldChange}
                    required
                  />
                </div>
              </div>

              <div className="volunteer-apply-field volunteer-apply-field--full">
                <label className="volunteer-apply-label" htmlFor="mensaje">
                  Dejar un mensaje
                </label>
                <textarea
                  id="mensaje"
                  name="mensaje"
                  className="volunteer-apply-input volunteer-apply-textarea"
                  placeholder="Deja un mensaje (Opcional)"
                  rows={4}
                  value={formData.mensaje}
                  onChange={handleFieldChange}
                />
              </div>

              {formMessage ? <p className="volunteer-apply-message">{formMessage}</p> : null}

              <button type="submit" className="volunteer-apply-submit" disabled={isSubmitting}>
                {isSubmitting ? 'Enviando...' : 'Enviar'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VolunteerPage;
